import type { CSSProperties, ReactNode } from 'react'
import { Fragment } from 'react'

import { styled } from 'styled-components'

import { Link } from '@b-stock/fe-core/bstock-react'
import {
  isLexicalTableNode,
  LexicalTable,
  type LexicalNode,
} from '@b-stock/fe-core/cms-blocks'
import { Colors, Typography } from '@b-stock/fe-core/design-system'

import {
  extractLexicalLinkUrl,
  isSignupAccountHref,
  resolveSellerProfileLexicalHref,
} from '@/helpers/telemetry/signupLinkTracking'

import { SignupTrackedLink } from './SignupTrackedLink'
import { StyledParagraph, SubHeader } from './ui'

/**
 * Lexical-only lists: restore markers on direct `li` (global reset clears them).
 * When a nested list is stored as an invalid `ul > ul` / `ol > ol`, parsers insert
 * a wrapper `li` whose only child is the inner list — that `li` still matched `> li`
 * and showed an extra top-level bullet on the first nested row. Hide markers on
 * those wrapper rows only (parent items that also have a paragraph still show a bullet).
 */
const LexicalBulletList = styled.ul`
  margin: 0 0 1rem;
  padding-left: 1.25rem;
  list-style-position: outside;

  & > li {
    display: list-item;
    list-style-type: disc;
  }

  & > li:has(> ul:only-child),
  & > li:has(> ol:only-child) {
    list-style-type: none !important;
  }

  /* Nested lists: circle; top-level stays disc (see & > li above). */
  & li > ul > li,
  & li > ol > li {
    display: list-item;
    list-style-type: circle;
  }

  /* Invalid ul>ul / ul>ol (nested list not wrapped in li). */
  & > ul > li {
    display: list-item;
    list-style-type: circle;
  }

  & > ol > li {
    display: list-item;
    list-style-type: decimal;
  }

  & > li > ul,
  & > li > ol {
    margin-top: 0.35rem;
    margin-bottom: 0.25rem;
  }

  & > ul,
  & > ol {
    margin-top: 0.35rem;
    margin-bottom: 0.25rem;
    padding-left: 1.25rem;
    list-style-type: none;
  }
`

const LexicalNumberedList = styled.ol`
  margin: 0 0 1rem;
  padding-left: 1.25rem;
  list-style-position: outside;

  & > li {
    display: list-item;
    list-style-type: decimal;
  }

  & > li:has(> ul:only-child),
  & > li:has(> ol:only-child) {
    list-style-type: none !important;
  }

  /* Bullet sublists inside a numbered list use circles. */
  & li > ul > li {
    display: list-item;
    list-style-type: circle;
  }

  & > ul > li {
    display: list-item;
    list-style-type: circle;
  }

  & > ol > li {
    display: list-item;
    list-style-type: decimal;
  }

  & > li > ul,
  & > li > ol {
    margin-top: 0.35rem;
    margin-bottom: 0.25rem;
  }

  & > ul,
  & > ol {
    margin-top: 0.35rem;
    margin-bottom: 0.25rem;
    padding-left: 1.25rem;
    list-style-type: none;
  }
`

const LexicalListItem = styled.li`
  margin: 0 0 0.35rem;
`

const LexicalHr = styled.hr`
  margin: 1.5rem 0;
  border: 0;
  border-top: 0.0625rem solid ${Colors.Semantic.Neutral.medium_grey};
`

const BlockQuote = styled.blockquote`
  margin: 1rem 0;
  padding-left: 1rem;
  border-left: 0.25rem solid ${Colors.Semantic.Neutral.medium_grey};
  ${Typography.Body2}
`

const Heading3 = styled.h3`
  ${Typography.Title3}
  margin: 1.25rem 0 0.5rem;
`

const Heading4 = styled.h4`
  ${Typography.Body1_Semibold}
  margin: 1rem 0 0.5rem;
`

/**
 * The CMS's small-text block in its two sizes: Footnote (`Body4`, 12px) for the note under a
 * grading table, Caption (`Caption`, 10px). Spaced like `StyledParagraph` so it sits in the flow.
 */
const SmallTextParagraph = styled.p<{ $variant: 'caption' | 'footnote' }>`
  ${({ $variant }) =>
    $variant === 'caption' ? Typography.Caption : Typography.Body4}
  margin-bottom: 1rem;
`

/** The block's stored variant; content saved before variants existed is a footnote. */
function smallTextVariant(
  node: Record<string, unknown>
): 'caption' | 'footnote' {
  return node.variant === 'caption' ? 'caption' : 'footnote'
}

/**
 * fe-core owns the table itself (borders, header cells, spans, per-cell fill, the mobile rule);
 * this only spaces it like a paragraph inside a storefront tab.
 */
const StorefrontLexicalTable = styled(LexicalTable)`
  margin: 1rem 0;
`

const IS_BOLD = 1
const IS_ITALIC = 2
const IS_STRIKETHROUGH = 4
const IS_UNDERLINE = 8

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null
}

function getChildren(node: unknown): unknown[] {
  if (!isRecord(node)) return []
  const ch = node.children
  return Array.isArray(ch) ? ch : []
}

/**
 * Lexical should only emit `ul > li` / `ol > li`, but some exports place a nested
 * `list` as a sibling of `listitem`. That yields invalid `ul > ul` in HTML; parsers
 * insert an implicit wrapper `li` that still matches our outer `> li` bullet rule,
 * so the first nested row shows two markers. Merge orphan lists into the previous
 * item, or prepend to the next `listitem`, matching Payload's tree shape.
 */
function normalizeLexicalListChildren(raw: unknown[]): unknown[] {
  const pendingLists: unknown[] = []
  const result: unknown[] = []

  const flushPendingIntoListItem = (listitem: Record<string, unknown>) => {
    if (pendingLists.length === 0) return listitem
    return {
      ...listitem,
      children: [...pendingLists, ...getChildren(listitem)],
    }
  }

  for (const node of raw) {
    if (!isRecord(node)) continue
    if (node.type === 'list') {
      const prev = result[result.length - 1]
      if (prev && isRecord(prev) && prev.type === 'listitem') {
        result[result.length - 1] = {
          ...prev,
          children: [...getChildren(prev), node],
        }
      } else {
        pendingLists.push(node)
      }
      continue
    }
    if (node.type === 'listitem') {
      const withPending = flushPendingIntoListItem(node)
      pendingLists.length = 0
      result.push(withPending)
      continue
    }
    result.push(node)
  }

  for (const list of pendingLists) {
    result.push({
      type: 'listitem',
      version: 1,
      value: 1,
      format: '',
      children: [list],
    })
  }

  return result
}

/**
 * A block's horizontal alignment, as the editor's align button stores it: `format` on the
 * paragraph or heading, a word or empty. Only the four CSS values are passed on; anything else
 * in a document falls back to the stylesheet, which inside a table cell means left.
 */
function blockAlign(node: Record<string, unknown>): CSSProperties | undefined {
  const format = node.format
  return format === 'center' ||
    format === 'right' ||
    format === 'left' ||
    format === 'justify'
    ? { textAlign: format }
    : undefined
}

function wrapText(text: string, format: number): ReactNode {
  let el: ReactNode = text
  if (format & IS_STRIKETHROUGH) el = <s>{el}</s>
  if (format & IS_UNDERLINE) el = <u>{el}</u>
  if (format & IS_ITALIC) el = <em>{el}</em>
  if (format & IS_BOLD) el = <strong>{el}</strong>
  return el
}

/** Six hex digits, as the CMS stores a text colour; the hash is put back here. */
const TEXT_COLOR_TOKEN = /^[0-9a-f]{6}$/i

/**
 * Text colour is Lexical node state under `$.color`. Payload's TextState stores the colour's
 * name, mapped here to the same design-system token the CMS paints with; content saved by the
 * earlier feature holds six hex digits and still draws. Anything else is ignored rather than
 * passed to CSS, since the value comes from a document.
 */
const TEXT_COLOR_BY_NAME: Record<string, string> = {
  blue: Colors.Semantic.Info.highlight,
  gray: Colors.Semantic.Neutral.dark_grey,
  green: Colors.Semantic.Success.copy,
  orange: Colors.Semantic.Warning.highlight,
  red: Colors.Semantic.Error.highlight,
}

function textColor(node: Record<string, unknown>): string | null {
  const state = node.$
  if (!isRecord(state) || typeof state.color !== 'string') return null
  const named = TEXT_COLOR_BY_NAME[state.color]
  if (named) return named
  return TEXT_COLOR_TOKEN.test(state.color)
    ? `#${state.color.toUpperCase()}`
    : null
}

function renderLexicalNode(
  node: unknown,
  keyPath: string,
  signUpCtaLocation?: string
): ReactNode {
  if (!isRecord(node)) return null
  const type = typeof node.type === 'string' ? node.type : ''
  const children = getChildren(node)
  // Unknown or structural nodes render whatever is inside them and nothing of their own.
  const renderChildrenOnly = (): ReactNode =>
    children.length > 0 ? (
      <Fragment key={keyPath}>
        {children.map((c, i) =>
          renderLexicalNode(c, `${keyPath}.${i}`, signUpCtaLocation)
        )}
      </Fragment>
    ) : null

  switch (type) {
    case 'root':
      return (
        <Fragment key={keyPath}>
          {children.map((c, i) =>
            renderLexicalNode(c, `${keyPath}.${i}`, signUpCtaLocation)
          )}
        </Fragment>
      )
    case 'paragraph':
      return (
        <StyledParagraph key={keyPath} style={blockAlign(node)}>
          {children.map((c, i) =>
            renderLexicalNode(c, `${keyPath}.${i}`, signUpCtaLocation)
          )}
        </StyledParagraph>
      )
    case 'heading': {
      const tag =
        typeof node.tag === 'string' && /^h[2-4]$/.test(node.tag)
          ? node.tag
          : 'h2'
      const inner = children.map((c, i) =>
        renderLexicalNode(c, `${keyPath}.${i}`, signUpCtaLocation)
      )
      const style = blockAlign(node)
      if (tag === 'h3') {
        return (
          <Heading3 key={keyPath} style={style}>
            {inner}
          </Heading3>
        )
      }
      if (tag === 'h4') {
        return (
          <Heading4 key={keyPath} style={style}>
            {inner}
          </Heading4>
        )
      }
      return (
        <SubHeader key={keyPath} style={style}>
          {inner}
        </SubHeader>
      )
    }
    case 'text': {
      const text = typeof node.text === 'string' ? node.text : ''
      const format = typeof node.format === 'number' ? node.format : 0
      const color = textColor(node)
      const formatted = wrapText(text, format)
      return (
        <Fragment key={keyPath}>
          {color ? <span style={{ color }}>{formatted}</span> : formatted}
        </Fragment>
      )
    }
    // The CMS's small-text block, a paragraph type of its own (see `SmallTextParagraph`).
    case 'smalltext':
      return (
        <SmallTextParagraph
          $variant={smallTextVariant(node)}
          key={keyPath}
          style={blockAlign(node)}
        >
          {children.map((c, i) =>
            renderLexicalNode(c, `${keyPath}.${i}`, signUpCtaLocation)
          )}
        </SmallTextParagraph>
      )
    case 'linebreak':
      return <br key={keyPath} />
    case 'horizontalrule':
      return <LexicalHr key={keyPath} />
    case 'list': {
      const listType = node.listType === 'number' ? 'number' : 'bullet'
      const tag = node.tag === 'ol' || node.tag === 'ul' ? node.tag : null
      const ListTag =
        tag === 'ol' || (tag === null && listType === 'number')
          ? LexicalNumberedList
          : LexicalBulletList
      const normalized = normalizeLexicalListChildren(children)
      return (
        <ListTag key={keyPath}>
          {normalized.map((c, i) =>
            renderLexicalNode(c, `${keyPath}.${i}`, signUpCtaLocation)
          )}
        </ListTag>
      )
    }
    case 'listitem':
      return (
        <LexicalListItem key={keyPath}>
          {children.map((c, i) =>
            renderLexicalNode(c, `${keyPath}.${i}`, signUpCtaLocation)
          )}
        </LexicalListItem>
      )
    case 'quote':
      return (
        <BlockQuote key={keyPath}>
          {children.map((c, i) =>
            renderLexicalNode(c, `${keyPath}.${i}`, signUpCtaLocation)
          )}
        </BlockQuote>
      )
    case 'link':
    case 'autolink': {
      const fields = isRecord(node.fields) ? node.fields : {}
      const raw = extractLexicalLinkUrl(fields)
      const safeHref = resolveSellerProfileLexicalHref(raw)
      const newTab = fields.newTab === true && safeHref != null
      const renderedChildren = children.map((c, i) =>
        renderLexicalNode(c, `${keyPath}.${i}`, signUpCtaLocation)
      )
      if (!safeHref) {
        return <span key={keyPath}>{renderedChildren}</span>
      }
      const tabAttrs = newTab
        ? { target: '_blank' as const, rel: 'noopener noreferrer' as const }
        : {}
      if (signUpCtaLocation && isSignupAccountHref(safeHref)) {
        return (
          <SignupTrackedLink
            key={keyPath}
            href={safeHref}
            signUpCtaLocation={signUpCtaLocation}
            {...tabAttrs}
          >
            {renderedChildren}
          </SignupTrackedLink>
        )
      }
      return (
        <Link key={keyPath} href={safeHref} {...tabAttrs}>
          {renderedChildren}
        </Link>
      )
    }
    case 'table': {
      const candidate = node as LexicalNode
      if (isLexicalTableNode(candidate)) {
        return (
          <StorefrontLexicalTable
            key={keyPath}
            node={candidate}
            // Cell children go back through this walker, so a tracked sign-up link inside a
            // cell fires the same telemetry as one in a paragraph.
            renderCellContent={(cell, { row, column }) =>
              cell.children.map((c, i) =>
                renderLexicalNode(
                  c,
                  `${keyPath}.${row}.${column}.${i}`,
                  signUpCtaLocation
                )
              )
            }
          />
        )
      }
      return renderChildrenOnly()
    }
    // Inside a table these belong to LexicalTable; on their own they hold nothing but children.
    case 'tablerow':
    case 'tablecell':
      return renderChildrenOnly()
    case 'upload':
    case 'relationship':
      return null
    default:
      return renderChildrenOnly()
  }
}

type SellerProfileLexicalRichTextProps = {
  data: unknown
  /** When set, signup links (`/acct/signup`) fire GTM `sign_up_start` with this `cta_location`. */
  signUpCtaLocation?: string
}

/** Renders Payload Lexical JSON (seller profile fields) to simple HTML-like React. */
export function SellerProfileLexicalRichText({
  data,
  signUpCtaLocation,
}: SellerProfileLexicalRichTextProps) {
  if (!isRecord(data) || data.root === undefined) return null
  const root = data.root
  return <>{renderLexicalNode(root, 'lexical', signUpCtaLocation)}</>
}
